import HLS from 'hls.js';

import { SimuliveVideoRoomState } from '.';
import { SimuliveVideoRoomGetters } from './getters';
import { SimuliveVideoRoomMutations } from './mutations';
import {
  getIdealTestRunEndTimeStamp,
  getBroadcastVideoIdealPlaybackEndTimestamp,
  getInHouseSimuliveSource,
  addVideoPlaybackListeners,
  playSimuliveUsingHLSJS,
  playSimuliveUsingNativeHLSPlayback,
  hasSessionEnded,
  clearVideoPlaybackStallBufferTimeout,
  clearManifestLoadingErrorTimeout,
  clearVideoPlaybackNudgeTimeout,
  clearVideoPlaybackEndInterval,
  getTotalDurationOfAllVideoAssets,
  armPlaybackWatchdog,
  clearPlaybackWatchdogTimeout
} from './utils';
import { Broadcast } from '../event/types';

import { simuliveApiClient } from '@/services/apiClient';
import loggerService from '@/services/logger.service';
import trackerService from '@/services/tracker.service';
import { RootState } from '@/store';
import { getCurrentRoute } from '@/utils/routerAccess';

export interface SimuliveVideoRoomContext {
  state: SimuliveVideoRoomState;
  rootState: RootState;
  getters: SimuliveVideoRoomGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof SimuliveVideoRoomMutations>(
    mutation: k | 'liveVideoRoom/setIsHLSStreamingSupported' | 'event/stage/toggleMuted',
    payload?: any,
    config?: { root: boolean }
  ) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
}

const actions = {
  playVideoForSimuliveBroadcast(context: SimuliveVideoRoomContext, broadcastID: string) {
    if (!broadcastID) {
      return;
    }
    const currentBroadcastRoute = getCurrentRoute().params.id;
    if (!currentBroadcastRoute) {
      loggerService.warn('No current broadcast route found!');
      return;
    }
    if (currentBroadcastRoute !== broadcastID) {
      loggerService.warn('Current broadcast route does not match the broadcast id!');
      return;
    }

    // If Simulive has been triggered for the same broadcast previously
    // do not trigger it again.
    if (broadcastID === context.state.currentActiveSimuliveBroadcastID) {
      return;
    }
    const broadcastDetails = context.rootGetters['event/currentEventBroadcast'](broadcastID);

    const hasUserChoosenToForceWatchInHouseSimulive = context.getters.shouldUseInhouseSimulive;

    const isBroadcastSimulive = broadcastDetails.broadcast_type === 'SIMULIVE';
    const isUserForceWatchingInHouseSimulive = isBroadcastSimulive && hasUserChoosenToForceWatchInHouseSimulive;
    if (!isUserForceWatchingInHouseSimulive) {
      return;
    }

    const currentDateTimeStamp = (context.rootState.event.stage?.heartBeatDate || new Date()).getTime();

    const testRunEndTimeStamp = getIdealTestRunEndTimeStamp(context, broadcastDetails);
    const simuliveIdealPlaybackEndTimeStamp = getBroadcastVideoIdealPlaybackEndTimestamp(broadcastDetails);

    const inTestingMode = currentDateTimeStamp < testRunEndTimeStamp;
    const idealPlaybackEndTimestamp = inTestingMode ? testRunEndTimeStamp : simuliveIdealPlaybackEndTimeStamp;

    // If the simulive broadcast's playback should have ended by now return early dont start the video playback again.
    // Destroy is intentionally deferred until after this check so that
    // currentActiveSimuliveBroadcastID is never cleared on an early exit —
    // an empty ID would break purgeAndStartVideoPlaybackAgain.
    if (idealPlaybackEndTimestamp < currentDateTimeStamp) {
      loggerService.info(
        'Playback will not start because ideal playback time is in the past: ',
        currentDateTimeStamp,
        idealPlaybackEndTimestamp
      );
      return;
    }

    // All checks passed — safe to tear down the existing context now.
    context.dispatch('destroySimuliveBroadcastPlaybackContext');

    context.state.inTestingMode = inTestingMode;
    context.state.idealPlaybackEndTimestamp = idealPlaybackEndTimestamp;

    trackerService.track('inHouseSimulive', 'shouldBeUsed', { isEnabled: true });
    trackerService.track('inHouseSimulive', 'connectionInitialized', { timestamp: Date.now() });

    // destroy mediastore hls elm if exists here before we create a new one for inhouse simulive.
    context.dispatch('liveVideoRoom/cleanupLiveVideoStore', null, { root: true });
    context.state.currentActiveSimuliveBroadcastID = broadcastID;
    context.state.hLSVideoSource = getInHouseSimuliveSource(broadcastDetails);
    addVideoPlaybackListeners(context);
    const hlsJsSupported = HLS.isSupported();
    const nativeHlsSupport = context.state.hlsVideoElement.canPlayType('application/vnd.apple.mpegurl');
    loggerService.info(
      `[simulive-debug] playback branch decision: hlsJsSupported=${hlsJsSupported}, nativeHlsCanPlayType="${nativeHlsSupport}", userAgent="${navigator.userAgent}", inIframe=${window.self !== window.top}, visibilityState=${document.visibilityState}`
    );
    if (hlsJsSupported) {
      loggerService.info('Starting simulive playback using hls.js!');
      armPlaybackWatchdog(context);
      playSimuliveUsingHLSJS(context);
    } else if (nativeHlsSupport) {
      loggerService.info('Starting simulive playback using native playback!');
      armPlaybackWatchdog(context);
      playSimuliveUsingNativeHLSPlayback(context);
    } else {
      loggerService.warn(
        'Cannot play hls video because neither hls js is supported nor we have native support for hls playback!'
      );
      context.commit('liveVideoRoom/setIsHLSStreamingSupported', false);
    }
    hasSessionEnded(context);
  },
  destroySimuliveBroadcastPlaybackContext(context: SimuliveVideoRoomContext) {
    if (!context.state) {
      return;
    }

    loggerService.info(
      `[simulive-debug] destroySimuliveBroadcastPlaybackContext called, currentActiveSimuliveBroadcastID="${context.state.currentActiveSimuliveBroadcastID}", hLSContext exists=${!!context.state.hLSContext}`
    );

    context.state.currentActiveSimuliveBroadcastID = '';
    if (context.state.hLSContext) {
      context.state.hLSContext.destroy();
      context.state.hLSContext = null;
    }
    context.state.hlsVideoElement = document.createElement('video');
    context.state.hlsVideoElement.muted = context.rootState.event.stage?.muted;
    context.state.isFillerSlideVisible = true;
    context.state.hLSVideoSource = '';
    context.state.currentFragementPosition = 0;
    context.state.fragsThatFailedLoading = [];
    context.state.networkErrorRetriesCount = 0;
    context.state.hasVideoPlaybackStalled = false;
    context.state.isPlayingNatively = false;
    context.state.inTestingMode = false;
    context.state.isSessionEnded = true;

    clearVideoPlaybackStallBufferTimeout(context);
    clearManifestLoadingErrorTimeout(context);
    clearVideoPlaybackNudgeTimeout(context);
    clearVideoPlaybackEndInterval(context);
    clearPlaybackWatchdogTimeout(context);
  },
  updateHLSPlayerMuteState(context: SimuliveVideoRoomContext) {
    context.state.hlsVideoElement.muted = context.rootState.event.stage?.muted;
  },
  setActiveSpeakerDevice(context: SimuliveVideoRoomContext) {
    const speakerDeviceID = context.rootState.mediaroom.currentDevices?.speaker?.deviceId;

    if (!speakerDeviceID || !context.state.hlsVideoElement) {
      return;
    }

    try {
      context.state.hlsVideoElement.setSinkId(speakerDeviceID).catch(function (error: Error) {
        let errorMessage = error.message;
        if (error.name === 'SecurityError') {
          errorMessage = 'You need to use HTTPS for selecting audio output ' + 'device: ' + error.message;
        }
        throw errorMessage;
      });
    } catch (error) {
      loggerService.warn('error setting active speaker:', error);
    }
  },
  purgeAndStartVideoPlaybackAgain(context: SimuliveVideoRoomContext) {
    loggerService.info('Purging and starting video playback again!');
    const broadcastID = context.state.currentActiveSimuliveBroadcastID || getCurrentRoute().params.id;
    context.dispatch('destroySimuliveBroadcastPlaybackContext');
    context.dispatch('playVideoForSimuliveBroadcast', broadcastID);
  },
  checkAndResetSimuliveRoomPlayback(
    context: SimuliveVideoRoomContext,
    {
      oldBroadcastData,
      newBroadcastData
    }: {
      oldBroadcastData: Broadcast;
      newBroadcastData: Broadcast;
    }
  ) {
    if (!oldBroadcastData || !newBroadcastData) {
      return;
    }

    const hasSimuliveTestRunEndTimeChanged =
      oldBroadcastData.simulive_test_run_end_time !== newBroadcastData.simulive_test_run_end_time;
    const hasVideoAssetsLengthChanged =
      getTotalDurationOfAllVideoAssets(oldBroadcastData.video_assets) !==
      getTotalDurationOfAllVideoAssets(newBroadcastData.video_assets);
    const hasSimuliveSessionStartTimeChanged = oldBroadcastData.start_time !== newBroadcastData.start_time;
    const hasLivenessChanged = oldBroadcastData.is_live !== newBroadcastData.is_live;

    // When MM is unhealthy the recorder relies on uber polling for broadcast
    // updates. If nothing changed between polls (is_live/assets/times all
    // stable) none of the flags above fire, yet playback may never have
    // started because the initial play attempt failed (e.g. empty ID from a
    // prior early-exit). Detect that gap and kick off a fresh attempt.
    const isPlaybackExpectedButMissing =
      !context.state.currentActiveSimuliveBroadcastID &&
      newBroadcastData.is_live &&
      newBroadcastData.broadcast_type === 'SIMULIVE' &&
      context.getters.shouldUseInhouseSimulive;

    if (
      hasSimuliveTestRunEndTimeChanged ||
      hasVideoAssetsLengthChanged ||
      hasSimuliveSessionStartTimeChanged ||
      hasLivenessChanged ||
      isPlaybackExpectedButMissing
    ) {
      loggerService.info(
        `[simulive-debug] checkAndResetSimuliveRoomPlayback triggering purge. Reasons: testRunChanged=${hasSimuliveTestRunEndTimeChanged}, assetsChanged=${hasVideoAssetsLengthChanged}, startTimeChanged=${hasSimuliveSessionStartTimeChanged}, livenessChanged=${hasLivenessChanged}, playbackMissing=${isPlaybackExpectedButMissing} (old=${oldBroadcastData.is_live} -> new=${newBroadcastData.is_live})`
      );
      context.dispatch('purgeAndStartVideoPlaybackAgain');
    }
  },
  async getVTTFlagDetails(context: SimuliveVideoRoomContext, broadcastDetails: Broadcast) {
    const response = await simuliveApiClient.get(
      `/simulive/${broadcastDetails?.event}/${broadcastDetails?.id}/metadata`,
      {},
      { headers: {} }
    );
    if (response) {
      context.commit('setShowOverlayCaptions', response.data?.showOverlayCaptions ?? true);
    }
  },
  changeVTTCaptionLanguage(context: SimuliveVideoRoomContext, language: string) {
    const videoElement = context.state.hlsVideoElement;
    const tracks = [...videoElement.textTracks];
    const activeTrack = Array.from(tracks).find((track: any) => track?.mode === 'showing');
    if (activeTrack) {
      activeTrack.mode = 'disabled';
    }
    const selectedTrack = tracks.find((track: any) => track.language === language);
    if (selectedTrack) {
      selectedTrack.mode = 'showing';
    }
  }
};

export default actions;
